import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as forgerock from '@forgerock/javascript-sdk';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { PingTokenResponse } from '../interfaces/auth.interfaces';

@Injectable()
export class LoginService {
  public loginUrl = environment.loginUrl;
  public adminUrl = environment.adminUrl;

  private readonly pingTokenEndpoint = environment.AuthConfig.refreshUrl;

  constructor(private readonly Http: HttpClient) {}

  // Used for the first login to extract the info and send it to the backend for auth
  async postWithPingAuth(
    endpoint: string,
    pingIdToken: string,
    body?: any,
  ) {
    if (!pingIdToken) {
      throw new Error('No ID token available');
    }

    return firstValueFrom(
      this.Http.request('POST', `${this.loginUrl}/${endpoint}`, {
        body: body,
        headers: {
          Authorization: `Bearer ${pingIdToken}`,
          'Content-Type': 'application/json',
        },
      }),
    );
  }

  // Used for refreshing the backend token after fetching a new access token from Ping
  async postWithPingAccessToken(endpoint: string, pingAccessToken: string) {
    return firstValueFrom(
      this.Http.request('POST', `${this.loginUrl}/${endpoint}`, {
        headers: {
          Authorization: `Bearer ${pingAccessToken}`,
          'Content-Type': 'application/json',
        },
      }),
    );
  }

  // Used for posting to Ping to get a new set of tokens
  async refreshPingTokens(
    refreshToken: string,
  ): Promise<forgerock.OAuth2Tokens | null> {
    try {
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: environment.AuthConfig.clientId,
      });

      const response = await firstValueFrom(
        this.Http.post<PingTokenResponse>(
          this.pingTokenEndpoint,
          body.toString(),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          },
        ),
      );

      if (!response?.access_token) {
        return null;
      }

      const tokens: forgerock.OAuth2Tokens = {
        accessToken: response.access_token,
        idToken: response.id_token,
        refreshToken: response.refresh_token || refreshToken,
        tokenExpiry: Date.now() + response.expires_in * 1000,
      };
      if (environment.verboseLogging) {
        console.log('New tokens: ', tokens);
      }

      return tokens;
    } catch (error) {
      if (environment.verboseLogging) {
        console.error('Failed to refresh Ping tokens:', error);
      }
      return null;
    }
  }

  private async request(
    endpoint: string,
    method = 'GET',
    params?: {},
  ): Promise<any> {
    const options = this.httpOptions(method, { ...params });

    const url = endpoint;

    return firstValueFrom(this.Http.request(method, url, options));
  }

  private httpOptions(method: string, params?: { [key: string]: string }) {
    const optionKey = method === 'GET' ? 'params' : 'body';

    const options = Object.keys(params).reduce((response, key: string) => {
      if (params[key] !== null) {
        response[key] = params[key];
      }

      return response;
    }, {});

    return {
      [optionKey]: options,
    };
  }
}
